"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";

interface Passage {
  title: string;
  words: number;
  text: string;
}

const PASSAGES: Passage[] = [
  {
    title: "How Silent Reading Works",
    words: 231,
    text: `Silent reading is a surprisingly recent habit. For most of history, reading meant reading aloud: ancient scribes wrote texts as unbroken streams of letters, and readers sounded out the words to find where one ended and the next began. Spaces between words, punctuation, and lowercase letters were gradual inventions, spread by medieval copyists who wanted texts that the eye could navigate without help from the voice. Once words became visually distinct units, readers could take them in at a glance, and reading became something you could do quietly at your desk.

Modern eye-tracking research shows what that glance actually involves. Your eyes do not glide smoothly across a line of text. They move in quick jumps called saccades, each lasting a fraction of a second, separated by brief pauses called fixations. Almost all visual information is gathered during the fixations; during the jumps you are effectively blind. Skilled readers fixate on most words but skip short, predictable ones, and they occasionally jump backward to recheck something that did not fit.

This mechanical rhythm sets a practical ceiling on reading speed. You can widen the span of text you take in per fixation and reduce backward glances with practice, but claims of effortless reading at thousands of words per minute conflict with how the eye and brain actually process print. Faster reading usually means smarter sampling of the text, not faster seeing.`,
  },
  {
    title: "Why Rereading Fails and Testing Works",
    words: 228,
    text: `Students often study by rereading their notes until the material feels familiar. Cognitive scientists have tested this approach repeatedly, and the results are humbling: rereading produces a comforting sense of fluency but only weak gains in actual recall. The feeling of knowing something and the ability to retrieve it later are different things, and rereading mostly trains the first.

What works better is retrieval practice. Closing the book and forcing yourself to recall the material — with flashcards, practice questions, or simply writing down everything you remember — strengthens memory far more than another pass through the text. Each successful retrieval makes the next one easier, and even failed attempts help, because they show you precisely what you have not yet learned. Testing, in other words, is not just a way to measure learning. It is a way to produce it.

Timing matters as much as method. Cramming concentrates practice into one exhausting session, and whatever it builds tends to evaporate within days. Spacing the same hours of study across a week or a month produces memories that last, because each session begins after some forgetting has set in, and relearning partly forgotten material is what makes it durable.

The practical recipe is short: space your sessions, test yourself often, and treat the discomfort of struggling to remember as a sign the method is working rather than failing.`,
  },
  {
    title: "What to Cut When You Edit",
    words: 221,
    text: `Writers are often told to edit ruthlessly, but few are told what to cut first. A good starting point is the sentence's machinery: the words that carry no information of their own. Phrases like "in order to," "the fact that," and "it is important to note that" add syllables without adding meaning. Striking them rarely changes what a sentence says; it only changes how long the reader must wait to find out.

The second target is abstraction. Prose goes limp when its subjects are concepts and its verbs are forms of "to be." Compare "the implementation of the policy was met with resistance from staff" with "staff resisted the new policy." The revision is shorter, but the real improvement is that someone in the sentence is now doing something. Concrete subjects and active verbs give the reader an image to hold; abstractions ask the reader to do the writer's work.

None of this means every sentence must be short or plain. Variety keeps prose alive, and a long sentence that unspools an idea in careful stages can be a pleasure to follow. The goal of editing is not brevity for its own sake but a fair exchange: every word the reader spends attention on should pay something back. Cutting is simply how writers honor that bargain, one sentence at a time.`,
  },
];

type TestPhase = "idle" | "reading" | "done";

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const READING_SPEED_TEST_STRINGS = {
  passageReady:
    "A {count}-word passage titled “{title}” is ready. The timer starts the instant you press Start — read normally, don't race.",
  startReading: "Start Reading",
  doneIFinishedReading: "Done — I Finished Reading",
  yourResult: "Your Result",
  wordsPerMinute: "Words per minute",
  timeTaken: "Time taken",
  passageLength: "Passage length",
  passageWordCount: "one: {count} word|other: {count} words",
  benchmark: "Benchmark",
  speed: "Speed",
  retakeWithADifferentPassage: "Retake With a Different Passage",
  whatThisTestMeasures: "What This Test Measures",
  thisTestMeasuresSpeedOnly:
    "This test measures speed only — how quickly you moved through the passage — not comprehension. A high WPM is only meaningful if you could still summarize what you read. For the most reliable estimate, take the test a few times on different passages and average the results.",
  benchmarkAverageAdult: "Average adult, non-fiction (silent reading)",
  benchmarkAverageFiction: "Average adult, fiction (silent reading)",
  benchmarkSkimming: "Skimming (reduced comprehension)",
  bandBelowAverage: "Below average — a careful, deliberate pace",
  bandSlightlyBelowAverage: "Slightly below the average adult (~238 WPM)",
  bandAroundAverage: "Around the average adult reading speed",
  bandAboveAverage: "Above the average adult — a strong reading pace",
  bandSkimmingTerritory: "Skimming territory — check your comprehension",
} as const;

type Strings = typeof READING_SPEED_TEST_STRINGS;

function getBenchmarks(strings: Strings): { label: string; wpm: string }[] {
  return [
    { label: strings.benchmarkAverageAdult, wpm: "~238 WPM" },
    { label: strings.benchmarkAverageFiction, wpm: "~260 WPM" },
    { label: strings.benchmarkSkimming, wpm: "400+ WPM" },
  ];
}

function benchmarkBand(wpm: number, strings: Strings): string {
  if (wpm < 150) {
    return strings.bandBelowAverage;
  }
  if (wpm < 238) {
    return strings.bandSlightlyBelowAverage;
  }
  if (wpm < 260) {
    return strings.bandAroundAverage;
  }
  if (wpm < 400) {
    return strings.bandAboveAverage;
  }
  return strings.bandSkimmingTerritory;
}

export default function ReadingSpeedTestWidget() {
  const [phase, setPhase] = useState<TestPhase>("idle");
  const [passageIndex, setPassageIndex] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const passage = PASSAGES[passageIndex];
  const minutes = elapsedSeconds / 60;
  const wpm = minutes > 0 ? Math.round(passage.words / minutes) : 0;

  function handleStart() {
    setStartTime(Date.now());
    setPhase("reading");
  }

  function handleDone() {
    setElapsedSeconds((Date.now() - startTime) / 1000);
    setPhase("done");
  }

  function handleRetake() {
    setPassageIndex((prev) => (prev + 1) % PASSAGES.length);
    setElapsedSeconds(0);
    setPhase("idle");
  }

  const locale = useToolLocale();
  const strings = useToolUi(READING_SPEED_TEST_STRINGS);
  const fmt = useToolNumber();
  const benchmarks = getBenchmarks(strings);

  return (
    <>
      {/* Test area */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-6">
        {phase === "idle" && (
          <div className="space-y-4 text-center">
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t(strings.passageReady, {
                count: passage.words,
                title: passage.title,
              })}
            </p>
            <button
              className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
              onClick={handleStart}
              type="button"
            >
              {strings.startReading}
            </button>
          </div>
        )}
        {phase === "reading" && (
          <div className="space-y-4">
            <h2 className="font-semibold text-foreground text-lg">
              {passage.title}
            </h2>
            <div className="space-y-4 text-base text-foreground leading-relaxed">
              {passage.text.split("\n\n").map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </div>
            <button
              className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
              onClick={handleDone}
              type="button"
            >
              {strings.doneIFinishedReading}
            </button>
          </div>
        )}
        {phase === "done" && (
          <div className="space-y-4">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.yourResult}
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <StatCard label={strings.wordsPerMinute} value={wpm} />
              <StatCard
                label={strings.timeTaken}
                value={`${fmt(elapsedSeconds, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}s`}
              />
              <StatCard
                label={strings.passageLength}
                value={tPlural(strings.passageWordCount, locale, passage.words)}
              />
            </div>
            <p className="text-foreground text-sm">
              {benchmarkBand(wpm, strings)}
            </p>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border border-b bg-muted/30">
                    <th className="px-4 py-2 font-medium text-foreground">
                      {strings.benchmark}
                    </th>
                    <th className="px-4 py-2 font-medium text-foreground">
                      {strings.speed}
                    </th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  {benchmarks.map((b) => (
                    <tr className="border-border/50 border-b" key={b.label}>
                      <td className="px-4 py-2">{b.label}</td>
                      <td className="px-4 py-2">{b.wpm}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
              onClick={handleRetake}
              type="button"
            >
              {strings.retakeWithADifferentPassage}
            </button>
          </div>
        )}
      </section>
      {/* Honest note */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.whatThisTestMeasures}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.thisTestMeasuresSpeedOnly}
        </p>
      </section>
    </>
  );
}
